<!-- Notes Panel V2 Content -->
<!-- Note: Parent provides the sidebar/drawer container -->

<!-- Single subscription to notes observable, reused throughout template -->
@let notesList = (notes$ | async) ?? [];
@let filteredNotes = applyFiltersToNotes(notesList);
@let displayedNotes = applySearch(filteredNotes);
@let availableUsers = getAvailableUsers(notesList);

<div class="notes-panel-v2-content">
  <!-- Info Text -->
  <p class="notes-info-text">Notes will only be saved to Clear, not Byte.</p>

  <!-- Create New Note (input-style clickable element, when editor is closed) -->
  @if (!isEditorOpen()) {
    <div
      class="notes-create-input"
      [class.notes-create-input-disabled]="isLoading() || isSaving()"
      (click)="!isLoading() && !isSaving() && openCreateEditor()"
      tabindex="0"
      role="button"
      (keydown.enter)="!isLoading() && !isSaving() && openCreateEditor()"
      (keydown.space)="!isLoading() && !isSaving() && openCreateEditor()"
    >
      <span class="notes-create-input-text">Create New Note</span>
    </div>
  }

  <!-- Note Editor (for create/edit) -->
  @if (isEditorOpen()) {
    @let editorContent = (editorContent$ | async) ?? '';
    <div class="notes-editor">
      <!-- Category Tags (not shown for legacy notes — they don't support tags) -->
      @if (!isEditingLegacyNote()) {
        <div class="notes-editor-category">
          <cmg-clear2-ui-tags-chips
            [tagGuids]="editingTagGuids()"
            [pipelineType]="PipelineType.LoanNote"
            [postOnAddingTag]="false"
            [deleteOnRemovingTag]="false"
            [tagLocation]="'sidebar'"
            [isEditable]="true"
            [tagSortOrder]="loanNoteTagOrder"
            (tagAdded)="onTagsChanged($event)"
            (tagRemoved)="onTagsChanged($event)"
          />
        </div>
      }
      <div class="notes-editor-content" [class.notes-editor-content-error]="editorContent.length > maxNoteLength">
        <p-editor
          [ngModel]="editorContent"
          (ngModelChange)="onEditorContentChange($event)"
          [style]="{ 'min-height': '200px' }"
          placeholder="Enter your note..."
        >
          <ng-template pTemplate="header">
            <span class="ql-formats">
              <button type="button" class="ql-bold" aria-label="Bold"></button>
              <button type="button" class="ql-italic" aria-label="Italic"></button>
              <button type="button" class="ql-underline" aria-label="Underline"></button>
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-list" value="ordered" aria-label="Ordered List"></button>
              <button type="button" class="ql-list" value="bullet" aria-label="Bullet List"></button>
            </span>
          </ng-template>
        </p-editor>
        <div class="notes-editor-footer">
          <div class="notes-editor-footer-left">
            <span class="notes-char-count" [class.notes-char-count-over]="editorContent.length > maxNoteLength">
              {{ editorContent.length }} / {{ maxNoteLength }}
            </span>
            <span class="notes-char-error-message" [class.notes-char-error-visible]="editorContent.length > maxNoteLength">
              Max Character Limit of {{ maxNoteLength | number }} Characters Reached
            </span>
          </div>
          <div class="notes-editor-actions">
            <button pButton type="button" label="Cancel" class="btn-pill-secondary" [disabled]="isSaving()" (click)="closeEditor()"></button>
            <button
              pButton
              type="button"
              label="Save"
              class="btn-pill-primary"
              [loading]="isSaving()"
              [disabled]="!editorContent.trim() || editorContent.length > maxNoteLength"
              (click)="saveNote()"
            ></button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Notes Count & Filter/Search Row -->
  @if (!isLoading() && !errorMessage()) {
    <div class="notes-toolbar">
      <span class="notes-unread-count">
        @if (activeSearchQuery() || hasActiveFilters()) {
          {{ displayedNotes.length }} Results
        } @else {
          {{ notesList.length }} Notes
        }
      </span>
      <div class="notes-toolbar-actions">
        <!-- Filter button -->
        <button
          pButton
          type="button"
          icon="pi pi-filter"
          class="p-button-text p-button-rounded p-button-sm notes-toolbar-btn"
          [class.notes-toolbar-btn-active]="isFilterOpen() || hasActiveFilters()"
          [class.notes-toolbar-btn-has-filters]="hasActiveFilters()"
          pTooltip="Filter Notes"
          tooltipPosition="left"
          (click)="toggleFilter($event)"
        ></button>
        <!-- Search toggle button -->
        <button
          pButton
          type="button"
          icon="pi pi-search"
          class="p-button-text p-button-rounded p-button-sm notes-toolbar-btn"
          [class.notes-toolbar-btn-active]="isSearchOpen()"
          pTooltip="Search Notes"
          tooltipPosition="left"
          (click)="toggleSearch()"
        ></button>
      </div>
    </div>

    <!-- Search Bar (toggled by search button) -->
    @if (isSearchOpen()) {
      <div class="notes-search-bar">
        <div class="notes-search-input-wrapper">
          <input
            #searchInput
            type="text"
            class="notes-search-input"
            placeholder="Search Notes"
            [value]="searchTerm()"
            (input)="onSearchInput($event)"
            (keydown.enter)="executeSearch()"
          />
          @if (searchTerm() || activeSearchQuery()) {
            <button type="button" class="notes-search-clear-btn" (click)="clearSearch()" aria-label="Clear search">
              <i class="pi pi-times"></i>
            </button>
          }
        </div>
        <button
          pButton
          type="button"
          label="Search"
          class="btn-pill-primary notes-search-btn"
          [disabled]="searchTerm().length < 2"
          (click)="executeSearch()"
        ></button>
      </div>
    }

    <!-- Filter Tags Row (when filters are active, overflow hidden by CSS, revealed on hover) -->
    @if (hasActiveFilters()) {
      @let filterTags = getActiveFilterTags(availableUsers);
      <div class="notes-filter-tags">
        <span class="notes-filter-tags-label">Filtered by:</span>
        <div class="notes-filter-tags-list" [class.notes-filter-tags-list-overflow]="filterTags.length > 4">
          @for (tag of filterTags; track tag.label) {
            <span
              class="notes-filter-tag"
              role="button"
              tabindex="0"
              [attr.aria-label]="'Remove ' + tag.label + ' filter'"
              (click)="removeFilterTag(tag)"
              (keydown.enter)="removeFilterTag(tag)"
              (keydown.space)="removeFilterTag(tag); $event.preventDefault()"
              [ngStyle]="{
                '--tag-bg-color': tag.backgroundColor || null,
                '--tag-text-color': tag.textColor || null,
                '--tag-hover-bg-color': tag.hoverBackgroundColor || null,
                '--tag-hover-text-color': tag.hoverTextColor || null,
              }"
            >
              <span class="notes-filter-tag-text">{{ tag.label }}</span>
              <i class="pi pi-times notes-filter-tag-icon"></i>
            </span>
          }
          @if (filterTags.length > 4) {
            <span class="notes-filter-tags-overflow-count">+{{ filterTags.length - 4 }}</span>
          }
        </div>
      </div>
    }
  }

  <!-- Loading State -->
  @if (isLoading()) {
    <div class="notes-loading">
      <p-progressSpinner strokeWidth="4" [style]="{ width: '40px', height: '40px' }" />
      <span class="notes-loading-text">Loading notes...</span>
    </div>
  }

  <!-- Error State -->
  @if (errorMessage()) {
    <div class="notes-error">
      <i class="notes-error-icon pi pi-exclamation-triangle"></i>
      <span class="notes-error-text">{{ errorMessage() }}</span>
      <button pButton type="button" label="Retry" class="p-button-text p-button-sm" (click)="retry()"></button>
    </div>
  }

  <!-- Empty State (no notes at all) -->
  @if (!isLoading() && !errorMessage() && notesList.length === 0) {
    <div class="notes-empty-state">
      <span class="notes-empty-state-icon"></span>
      <span class="notes-empty-state-text">There are no notes</span>
    </div>
  }

  <!-- No Results (from search or filters) -->
  @if (!isLoading() && !errorMessage() && notesList.length > 0 && displayedNotes.length === 0 && (activeSearchQuery() || hasActiveFilters())) {
    <div class="notes-empty-state">
      <span class="notes-empty-state-icon"></span>
      <span class="notes-empty-state-text">No results found</span>
    </div>
  }

  <!-- Notes List -->
  @if (!isLoading() && !errorMessage() && displayedNotes.length > 0) {
    <div class="notes-list">
      @for (note of displayedNotes; track note.guid ?? $index) {
        @let canEdit = canEditNote(note);
        <div class="note-card" [class.note-card-saving]="isSaving()" [class.note-card-unread]="note.isUnread">
          <!-- Note Header -->
          <div class="note-card-header">
            <div class="note-card-author">
              @if (note.isUnread) {
                <span class="note-card-unread-dot"></span>
              }
              <span class="note-card-author-name">{{ note.insertedByName || 'System' }}</span>
              <!-- Legacy notes (from LoanColorMapping) don't have timestamps -->
              @if (note.dateInserted) {
                <span class="note-card-date">{{ note.dateInserted | date: 'M/d/yy, h:mm a' }}</span>
              }
              @if (isNoteEdited(note)) {
                <span
                  class="note-card-edited"
                  [pTooltip]="'Last Edited ' + (note.dateUpdated | date: 'M/d/yy, h:mm a')"
                  tooltipPosition="top"
                  tooltipStyleClass="tooltip-nowrap"
                  >Edited</span
                >
              }
            </div>
            <div class="note-card-actions">
              <!-- Pin/Unpin button (not available for legacy notes - they don't have GUIDs) -->
              @if (!note.isLegacy) {
                <button
                  pButton
                  type="button"
                  class="p-button-text p-button-rounded p-button-sm note-card-pin-btn"
                  [disabled]="isSaving()"
                  [pTooltip]="note.isPinned ? 'Unpin Note' : 'Pin Note to Top'"
                  [attr.aria-label]="note.isPinned ? 'Unpin Note' : 'Pin Note to Top'"
                  tooltipPosition="top"
                  (click)="togglePin(note)"
                >
                  <span class="note-icon" [class.note-icon-pin]="!note.isPinned" [class.note-icon-pin-filled]="note.isPinned"></span>
                </button>
              }
              <!-- Ellipsis menu for edit/delete -->
              <button
                pButton
                type="button"
                class="p-button-text p-button-rounded p-button-sm note-card-menu-btn"
                [class.note-card-menu-btn-disabled]="!canEdit"
                [disabled]="!canEdit || isSaving() || isEditorOpen()"
                [pTooltip]="!canEdit ? 'Cannot edit or delete other users\' notes.' : ''"
                tooltipPosition="left"
                (click)="canEdit && onNoteMenuClick($event, note)"
              >
                <span class="note-icon note-icon-ellipsis"></span>
              </button>
            </div>
          </div>

          <!-- Shared Note tag for legacy notes (from original Scratchpad) -->
          @if (note.isLegacy) {
            <div class="note-card-shared-row">
              <span
                class="note-card-shared"
                pTooltip="Pre-existing Scratchpad note. Editable by anyone."
                tooltipPosition="top"
                tooltipStyleClass="tooltip-nowrap"
                >Copied from Scratchpad</span
              >
            </div>
          }

          <!-- Category Tags (overflow hidden by CSS, revealed on hover) -->
          @if (note.tags?.length) {
            @let sortedTags = note.tags | sortNoteTags;
            <div class="note-card-categories" [class.note-card-categories-overflow]="sortedTags.length > 6">
              @for (tag of sortedTags; track tag.tagGUID) {
                <span
                  class="note-category-chip"
                  [ngStyle]="{
                    '--tag-bg-color': tag.backgroundColor,
                    '--tag-text-color': tag.textColor,
                    '--tag-hover-bg-color': tag.hoverBackgroundColor,
                    '--tag-hover-text-color': tag.hoverTextColor,
                  }"
                  >{{ tag.name }}</span
                >
              }
              @if (sortedTags.length > 6) {
                <span class="note-card-tags-overflow">+{{ sortedTags.length - 6 }}</span>
              }
            </div>
          }

          <!-- Note Content -->
          <div
            #noteContent
            class="note-card-content"
            [class.note-card-content-collapsed]="!isNoteExpanded(note.guid) && isNoteOverflowing(note.guid)"
            [innerHTML]="highlightSearchTerm(note.content)"
          ></div>

          <!-- View More/Less (for long notes) -->
          @if (isNoteOverflowing(note.guid)) {
            <button
              pButton
              type="button"
              [label]="isNoteExpanded(note.guid) ? 'View Less' : 'View More'"
              class="p-button-text p-button-sm note-card-toggle"
              (click)="toggleNoteExpanded(note.guid)"
            ></button>
          }
        </div>
      }
    </div>
  }
</div>

<!-- Filter Panel (popover triggered by filter button) -->
<p-popover #filterPanel styleClass="notes-filter-panel" (onShow)="onFilterPanelShow()" (onHide)="onFilterPanelHide()">
  <div class="filter-panel-content">
    <!-- Header -->
    <div class="filter-panel-header">
      <span class="filter-panel-title">Filter By</span>
    </div>

    <!-- Filter Options -->
    <div class="filter-panel-options">
      <!-- Other Type Section -->
      <div class="filter-section">
        <span class="filter-section-label">Other Type</span>
        <div class="filter-checkbox-group">
          <div class="filter-checkbox-item">
            <p-checkbox
              [ngModel]="pendingFilters().myNotes"
              (ngModelChange)="updatePendingFilter('myNotes', $event)"
              [binary]="true"
              inputId="filterMyNotes"
            />
            <label for="filterMyNotes" class="filter-checkbox-label">My Notes</label>
          </div>
          <div class="filter-checkbox-item">
            <p-checkbox
              [ngModel]="pendingFilters().unread"
              (ngModelChange)="updatePendingFilter('unread', $event)"
              [binary]="true"
              inputId="filterUnread"
            />
            <label for="filterUnread" class="filter-checkbox-label">Unread</label>
          </div>
          <div class="filter-checkbox-item">
            <p-checkbox
              [ngModel]="pendingFilters().sharedNotes"
              (ngModelChange)="updatePendingFilter('sharedNotes', $event)"
              [binary]="true"
              inputId="filterSharedNotes"
            />
            <label for="filterSharedNotes" class="filter-checkbox-label">Copied from Scratchpad</label>
          </div>
        </div>
      </div>

      <!-- Category Section -->
      <div class="filter-section">
        <span class="filter-section-label">Category</span>
        <div class="filter-dropdown-wrapper">
          <p-multiSelect
            [options]="(availableCategories$ | async) ?? []"
            [ngModel]="pendingFilters().tagGUIDs"
            (ngModelChange)="updatePendingFilter('tagGUIDs', $event)"
            optionLabel="name"
            optionValue="guid"
            placeholder="Select categories"
            [showClear]="true"
            class="filter-multiselect w-100"
            display="chip"
            chipIcon="pi pi-times"
            scrollHeight="150px"
          >
            <ng-template let-tag #item>
              <span
                class="note-category-chip"
                [ngStyle]="{
                  '--tag-bg-color': tag.backgroundColor,
                  '--tag-text-color': tag.textColor,
                  '--tag-hover-bg-color': tag.hoverBackgroundColor,
                  '--tag-hover-text-color': tag.hoverTextColor,
                }"
                >{{ tag.name }}</span
              >
            </ng-template>
            <ng-template let-selectedTags #selecteditems let-removeChip="removeChip">
              @for (tag of (selectedTags ?? []).slice(0, 2); track tag.guid) {
                <span
                  class="note-category-chip note-category-chip-selected"
                  [ngStyle]="{
                    '--tag-bg-color': tag.backgroundColor,
                    '--tag-text-color': tag.textColor,
                  }"
                >
                  {{ tag.name }}
                  <button type="button" class="note-category-chip-remove" [attr.aria-label]="'Remove ' + tag.name" (click)="removeChip(tag, $event)">
                    <i class="pi pi-times"></i>
                  </button>
                </span>
              }
              @if ((selectedTags ?? []).length > 2) {
                <span class="note-category-chip-overflow">+{{ selectedTags.length - 2 }}</span>
              }
            </ng-template>
          </p-multiSelect>
        </div>
      </div>

      <!-- Users Section -->
      <div class="filter-section">
        <span class="filter-section-label">Users</span>
        <div class="filter-dropdown-wrapper">
          <p-multiSelect
            [options]="availableUsers"
            [ngModel]="pendingFilters().userGuids"
            (ngModelChange)="updatePendingFilter('userGuids', $event)"
            optionLabel="name"
            optionValue="guid"
            placeholder="Select users"
            [showClear]="true"
            class="filter-multiselect w-100"
            display="chip"
            chipIcon="pi pi-times"
          />
        </div>
      </div>

      <!-- Date Range Section -->
      <div class="filter-section">
        <span class="filter-section-label">Date Range</span>
        <div class="filter-date-range">
          <p-datepicker
            [ngModel]="pendingFilters().dateFrom"
            (ngModelChange)="updatePendingFilter('dateFrom', $event)"
            dateFormat="mm/dd/yy"
            placeholder="mm/dd/yyyy"
            [showIcon]="true"
            class="filter-datepicker w-100"
            appendTo="body"
          />
          <span class="filter-date-separator">-</span>
          <p-datepicker
            [ngModel]="pendingFilters().dateTo"
            (ngModelChange)="updatePendingFilter('dateTo', $event)"
            dateFormat="mm/dd/yy"
            placeholder="mm/dd/yyyy"
            [showIcon]="true"
            class="filter-datepicker w-100"
            appendTo="body"
          />
        </div>
      </div>

      <!-- Clear All Filters -->
      <div class="filter-clear-all">
        <button pButton type="button" label="Clear All Filters" class="p-button-text p-button-sm" (click)="clearPendingFilters()"></button>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="filter-panel-footer">
      <button pButton type="button" label="Cancel" class="btn-pill-secondary" (click)="cancelFilters()"></button>
      <button pButton type="button" label="Apply" class="btn-pill-primary" (click)="applyFilters()"></button>
    </div>
  </div>
</p-popover>

<!-- Note Actions Menu (single instance, positioned on click) -->
<p-menu #noteMenu [popup]="true" [model]="noteMenuItems" appendTo="body" styleClass="note-actions-menu" />

<!-- Confirmation Dialog (key prevents conflict with parent dialogs) -->
<p-confirmDialog [key]="confirmDialogKey" styleClass="notes-delete-confirm-dialog" />
